import React, { useMemo } from "react";
import styled from "styled-components";
import { colors2 } from "oolib";
import { UI_BODY_SEMIBOLD_SM, UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION, UI_CAPTION_DF, UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { Fire, TrendUp } from "@phosphor-icons/react";
import { DashboardWidgetShell } from "@/_self_managed/SM_Dashboard/comps/DashboardWidgetShell";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { resolveZones } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/zoneStyles";
import { toneColor } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";
import { rankActFirst } from "./ActFirstPanel";

/**
 * The sub-topics in one zone (Fires, Emerging), ranked the way the act-first
 * list ranks — severity × momentum × volume — top five. Each row: the
 * sub-topic with a dot in its category's accent, its topic and observation
 * count, its count in each period of the trend window as small bars (the
 * selected period in the momentum's colour), and the change in count
 * between the comparison period and now. The zone's own label and hint
 * head the widget, so a tenant's copy overrides carry through.
 */
const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 56px;
  column-gap: 20px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${colors2.grey5};

  &:last-of-type {
    border-bottom: none;
  }
`;
const Title = styled.div`
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr);
  column-gap: 10px;
  align-items: start;
`;
const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-top: 5px;
`;
const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 36px;
`;
const Bar = styled.div`
  width: 12px;
  min-height: 3px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;
const Foot = styled.div`
  padding-top: 6px;
`;

const TOP_N = 5;
const ICONS = { fires: Fire, emerging: TrendUp };
const signed = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n)}`;

export const ZoneSubtopicsWidget = ({ zone, items, meta, blurNote, blurNoteIcon }) => {
  const categoryTheme = useSignalCategories();
  const copy = resolveZones(categoryTheme.prioritiesCopy)[zone];
  const Icon = ICONS[zone] || TrendUp;
  const rows = useMemo(() => rankActFirst(items.filter((item) => item.zone === zone)).slice(0, TOP_N), [items, zone]);
  const keys = meta.windowKeys || [];

  return (
    <DashboardWidgetShell
      titleJSX={
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 0 }}>
          <Icon size={16} color={copy.text} weight="bold" />
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.8rem", minWidth: 0 }}>
            <UI_BODY_SEMIBOLD_SM_DF style={{ color: copy.text }}>{copy.label}</UI_BODY_SEMIBOLD_SM_DF>
            <UI_CAPTION style={{ color: colors2.grey60 }}>{copy.hint}</UI_CAPTION>
          </div>
        </div>
      }
      style={{ gap: 0 }}
          blurNote={blurNote}
      blurNoteIcon={blurNoteIcon}
    >
      {rows.length === 0 && <UI_CAPTION style={{ color: colors2.grey60, padding: "12px 0" }}>Nothing in this zone for the period.</UI_CAPTION>}
      {rows.map((item) => {
        const counts = keys.map((k) => item.periodCounts?.[k] || 0);
        const max = Math.max(1, ...counts);
        const now = item.periodCounts?.[meta.nowKey] || 0;
        const then = meta.compareKey ? item.periodCounts?.[meta.compareKey] || 0 : null;
        const change = then === null ? null : now - then;
        const tone = change === null || change === 0 ? "flat" : change > 0 ? "up" : "down";
        const color = tone === "flat" ? colors2.grey50 : toneColor(tone);
        return (
          <Row key={item.nodeId}>
            <Title>
              <Dot $color={item.category ? categoryTheme.accentOf(item.category) : colors2.grey60} />
              <div style={{ minWidth: 0 }}>
                <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</UI_BODY_SEMIBOLD_SM>
                <UI_CAPTION style={{ color: colors2.grey60 }}>
                  {item.bucketLabel ? `${item.bucketLabel} · ` : ""}{item.n} obs
                </UI_CAPTION>
              </div>
            </Title>
            <Bars title={keys.map((k, i) => `${k}: ${counts[i]}`).join(" · ")}>
              {counts.map((n, i) => (
                <Bar key={keys[i]} $color={i === counts.length - 1 ? color : colors2.grey10} style={{ height: `${Math.max(8, (n / max) * 100)}%` }} />
              ))}
            </Bars>
            <UI_CAPTION_SEMIBOLD_DF style={{ color, textAlign: "right", whiteSpace: "nowrap" }}>{change === null ? "–" : signed(change)}</UI_CAPTION_SEMIBOLD_DF>
          </Row>
        );
      })}
      <Foot>
        <UI_CAPTION style={{ color: colors2.grey40 }}>
          {meta.prevLabel ? `Δ = observations ${meta.prevLabel} → ${meta.nowLabel}` : "Δ = observations; no earlier period to compare with"}
        </UI_CAPTION>
      </Foot>
    </DashboardWidgetShell>
  );
};
